import { IconButton, Text, Tooltip, type DataTableColumn } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { UPLOAD_STATUS_LABELS } from "@/components/dataset";
import { formatStorageTimestamp } from "./adminStorageFormatters";
import type { UploadRecoveryTableRow } from "./UploadRecoveryTableRow";
import styles from "./AdminStoragePage.module.css";

type UploadRecoveryColumnsOptions = {
  // 등록된 Dataset 이 있으면 상세 창을 연다(in-flight 라 대개 아직 없다).
  onOpenDataset: (datasetUuid: string) => void;
  // 행 "재큐". 확인 다이얼로그는 패널이 소유한다.
  onReprocess: (row: UploadRecoveryTableRow) => void;
};

export function createUploadRecoveryColumns({
  onOpenDataset,
  onReprocess,
}: UploadRecoveryColumnsOptions): readonly DataTableColumn<UploadRecoveryTableRow>[] {
  return [
    {
      key: "displayName",
      header: "업로드",
      width: "minmax(220px, 1fr)",
      render: (row) => (
        <Tooltip content={row.uuid}>
          <span className={styles.nameLink}>{row.displayName}</span>
        </Tooltip>
      ),
    },
    {
      key: "status",
      header: "상태",
      width: "150px",
      render: (row) => (
        <Text variant={hatcheryTextModes["body-normal"]}>{UPLOAD_STATUS_LABELS[row.status]}</Text>
      ),
    },
    {
      key: "importAttempts",
      header: "재점유",
      width: "110px",
      align: "right",
      render: (row) => (
        <Text variant={hatcheryTextModes["body-normal"]}>{formatReclaims(row)}</Text>
      ),
    },
    {
      key: "datasetUuid",
      header: "Dataset",
      width: "minmax(180px, 240px)",
      render: (row) => renderDataset(row, onOpenDataset),
    },
    {
      key: "cleanupStatus",
      header: "스토리지 회수",
      width: "130px",
      render: (row) =>
        row.kind === "stale"
          ? "—"
          : row.cleanupStatus === null
            ? "미접수"
            : {
                queued: "대기 중",
                running: "실행 중",
                succeeded: "완료",
                failed: "실패",
              }[row.cleanupStatus],
    },
    {
      key: "modifiedAt",
      header: "마지막 변경",
      width: "170px",
      align: "right",
      render: (row) => (
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.timestamp}>
          {formatStorageTimestamp(row.modifiedAt)}
        </Text>
      ),
    },
    {
      key: "uuid",
      header: "",
      width: "100px",
      align: "right",
      render: (row) =>
        row.kind === "stale" ? (
          <IconButton text="재큐" size="sm" variant="surface" onClick={() => onReprocess(row)} />
        ) : null,
    },
  ];
}

function formatReclaims(row: UploadRecoveryTableRow): string {
  return row.importAttempts === null
    ? "—"
    : `${row.importAttempts}회 (세대 ${row.item.import_generation})`;
}

// 데이터셋 셀. 등록 전이면 아직 행이 없다. 그래서 "—" 를 표시한다.
function renderDataset(
  row: UploadRecoveryTableRow,
  onOpenDataset: (datasetUuid: string) => void,
): ReactNode {
  const datasetUuid = row.datasetUuid;
  if (datasetUuid === null) {
    return <span className={styles.targetGone}>—</span>;
  }
  if (row.kind === "cancelled") return <span>{datasetUuid}</span>;
  return (
    <button
      type="button"
      className={styles.targetLink}
      title={datasetUuid}
      onClick={() => onOpenDataset(datasetUuid)}
    >
      {datasetUuid}
    </button>
  );
}
